/**
 * @file: useDashboardInspectionFlow.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useCurrentUser } from '@euler/app/flows/auth/context';
import { useInspectionTemplates } from '@euler/app/flows/task/functions';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useMostAppropriateInspectionTemplate } from '@euler/functions/useMostAppropriateInspectionTemplate';
import { sentry } from '@euler/lib/integration/sentry';
import {
  InspectionSiteInfo,
  VehicleInspectionFlow,
  kFaultFreeSiteCode,
  kFrontRecordsSiteCode,
  kRearRecordsSiteCode,
} from '@euler/model';
import {
  InspectionTemplateSceneType,
  SiteInspectionType,
} from '@euler/model/enum';
import { isNotNull } from '@euler/utils';
import { uniq } from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useFacadeInspectionTemplate = () => {
  const templates = useInspectionTemplates({
    scene: InspectionTemplateSceneType.Facade,
  });
  return useMostAppropriateInspectionTemplate(templates);
};

/**
 * Locates the facade inspection flow of the current pre-inspection. The flow
 * carries the template snapshot the pre-inspection was started with, so reading
 * its conf never needs the live template.
 *
 * `templateId` only disambiguates a pre-inspection that somehow holds more than
 * one facade flow, and resolving it costs a templates fetch that suspends -
 * callers that just want to read the snapshot pass nothing.
 */
export const findFacadeInspectionFlow = (
  flows: VehicleInspectionFlow[],
  templateId?: number,
) =>
  (templateId != null
    ? flows.find(x => x.template.originTemplateId === templateId)
    : undefined) ??
  flows.find(x => x.template.sceneType === InspectionTemplateSceneType.Facade);

export const useFacadeInspectionFlow = () => {
  const user = useCurrentUser();
  const {
    detail,
    taskManager,
    inventoryQuery: { getSiteById, getSiteByCode },
  } = useTaskContext();

  const [facadeSites, setFacadeSites] = useState<InspectionSiteInfo[]>();
  const template = useFacadeInspectionTemplate();
  const templateId = template?.id;
  const facadeInspectionFlow = useMemo(
    () => findFacadeInspectionFlow(detail.preInspection.flows, templateId),
    [detail.preInspection.flows, templateId],
  );

  useEffect(() => {
    if (!templateId) return;

    if (!facadeInspectionFlow) {
      taskManager.preInspectionManager
        .addInspectionFlow({
          templateId,
          assignToMemberId: user!.user.memberId,
        })
        .catch(err => {
          sentry.captureException(err);
          console.error(err);
        });
    } else {
      const categories = facadeInspectionFlow.template.conf.categories;
      const selectedSiteIds = categories.flatMap(x =>
        x.groups.flatMap(g => g.siteIds),
      );
      const requiredRecordSiteIds = [
        kFaultFreeSiteCode,
        kFrontRecordsSiteCode,
        kRearRecordsSiteCode,
      ]
        .map(code => getSiteByCode(code)?.id)
        .filter(isNotNull);
      for (const siteId of requiredRecordSiteIds) {
        if (!selectedSiteIds.includes(siteId)) {
          selectedSiteIds.push(siteId);
        }
      }
      const disabledSiteIdSet = new Set(
        categories.flatMap(x => x.groups.flatMap(g => g.disabledSiteIds ?? [])),
      );
      const selectedSites = uniq(
        selectedSiteIds.filter(x => !disabledSiteIdSet.has(x)),
      )
        .map(x => getSiteById(x))
        .filter(isNotNull);
      for (const site of selectedSites) {
        taskManager.preInspectionManager.beginSiteInspection({
          flowId: facadeInspectionFlow.id,
          site,
          inspectionType: SiteInspectionType.Facade,
          technicianId: user!.user.memberId,
          technicianName: user!.user.name,
          bundle: 'facade',
        });
      }
      setFacadeSites(selectedSites);
    }
  }, [
    facadeInspectionFlow,
    getSiteByCode,
    getSiteById,
    taskManager.preInspectionManager,
    templateId,
    user,
  ]);

  const endFacadeInspection = useCallback(() => {
    taskManager.preInspectionManager.endSiteInspectionBundle('facade');
  }, [taskManager.preInspectionManager]);

  const resetFacadeInspection = useCallback(() => {
    taskManager.preInspectionManager.resetSiteInspectionBundle('facade');
  }, [taskManager.preInspectionManager]);

  return {
    facadeSites,
    facadeInspectionFlow,
    endFacadeInspection,
    resetFacadeInspection,
  };
};
